import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import apiService from "../../services/api";
import { formatCurrency } from "../../utils/currencyUtils";

const TutorRateManager = ({ tutorId, tutorName, onClose }) => {
  const queryClient = useQueryClient();
  const [isCreating, setIsCreating] = useState(false);
  const [editingRateId, setEditingRateId] = useState(null);
  const [formData, setFormData] = useState({
    hourlyRate: "",
    effectiveYearMonth: "",
    notes: "",
  });

  // Fetch rate history
  const {
    data: rateHistory = [],
    isLoading: isLoadingHistory,
    error: historyError,
  } = useQuery({
    queryKey: ["tutorRateHistory", tutorId],
    queryFn: async () => {
      try {
        return await apiService.getTutorRateHistory(tutorId);
      } catch (error) {
        console.warn(
          `Could not fetch rate history for tutor ${tutorId}:`,
          error,
        );
        return [];
      }
    },
    enabled: !!tutorId,
  });

  // Fetch current rate
  const {
    data: currentRate,
    isLoading: isLoadingCurrent,
    error: currentError,
  } = useQuery({
    queryKey: ["tutorCurrentRate", tutorId],
    queryFn: async () => {
      try {
        return await apiService.getTutorCurrentRate(tutorId);
      } catch (error) {
        console.warn(
          `Could not fetch current rate for tutor ${tutorId}:`,
          error,
        );
        return null;
      }
    },
    enabled: !!tutorId,
  });

  // Fetch rate summary
  const { isLoading: isLoadingSummary } = useQuery({
    queryKey: ["tutorRateSummary", tutorId],
    queryFn: async () => {
      try {
        return await apiService.getTutorRateHistorySummary(tutorId);
      } catch (error) {
        console.warn(
          `Could not fetch rate summary for tutor ${tutorId}:`,
          error,
        );
        return null;
      }
    },
    enabled: !!tutorId,
  });

  // Create rate mutation
  const createRateMutation = useMutation({
    mutationFn: (rateData) =>
      apiService.createTutorRate({
        ...rateData,
        canonicalTutorId: tutorId,
      }),
    onSuccess: () => {
      toast.success("Rate created successfully");
      queryClient.invalidateQueries({
        queryKey: ["tutorRateHistory", tutorId],
      });
      queryClient.invalidateQueries({
        queryKey: ["tutorCurrentRate", tutorId],
      });
      queryClient.invalidateQueries({
        queryKey: ["tutorRateSummary", tutorId],
      });
      queryClient.invalidateQueries({ queryKey: ["tutors"] });
      setIsCreating(false);
      setFormData({
        hourlyRate: "",
        effectiveYearMonth: "",
        notes: "",
      });
    },
    onError: (error) => {
      toast.error(`Failed to create rate: ${error.message}`);
    },
  });

  // Update rate mutation
  const updateRateMutation = useMutation({
    mutationFn: ({ rateId, rateData }) =>
      apiService.updateTutorRate(rateId, rateData),
    onSuccess: () => {
      toast.success("Rate updated successfully");
      queryClient.invalidateQueries({
        queryKey: ["tutorRateHistory", tutorId],
      });
      queryClient.invalidateQueries({
        queryKey: ["tutorCurrentRate", tutorId],
      });
      queryClient.invalidateQueries({
        queryKey: ["tutorRateSummary", tutorId],
      });
      queryClient.invalidateQueries({ queryKey: ["tutors"] });
      setEditingRateId(null);
      setFormData({
        hourlyRate: "",
        effectiveYearMonth: "",
        notes: "",
      });
    },
    onError: (error) => {
      toast.error(`Failed to update rate: ${error.message}`);
    },
  });

  // Delete rate mutation
  const deleteRateMutation = useMutation({
    mutationFn: (rateId) => apiService.deleteTutorRate(rateId),
    onSuccess: () => {
      toast.success("Rate deleted successfully");
      queryClient.invalidateQueries({
        queryKey: ["tutorRateHistory", tutorId],
      });
      queryClient.invalidateQueries({
        queryKey: ["tutorCurrentRate", tutorId],
      });
      queryClient.invalidateQueries({
        queryKey: ["tutorRateSummary", tutorId],
      });
      queryClient.invalidateQueries({ queryKey: ["tutors"] });
    },
    onError: (error) => {
      toast.error(`Failed to delete rate: ${error.message}`);
    },
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCreateRate = () => {
    if (!formData.hourlyRate || !formData.effectiveYearMonth) {
      toast.error("Please fill in all required fields");
      return;
    }

    createRateMutation.mutate(formData);
  };

  const handleUpdateRate = () => {
    if (!formData.hourlyRate || !formData.effectiveYearMonth) {
      toast.error("Please fill in all required fields");
      return;
    }

    updateRateMutation.mutate({
      rateId: editingRateId,
      rateData: formData,
    });
  };

  const handleDeleteRate = (rateId) => {
    if (window.confirm("Are you sure you want to delete this rate?")) {
      deleteRateMutation.mutate(rateId);
    }
  };

  const handleEditRate = (rate) => {
    setEditingRateId(rate.id);
    setIsCreating(true);
    setFormData({
      hourlyRate: rate.hourlyRate.toString(),
      effectiveYearMonth: rate.effectiveYearMonth,
      notes: rate.notes || "",
    });
  };

  const handleCancelEdit = () => {
    setIsCreating(false);
    setEditingRateId(null);
    setFormData({
      hourlyRate: "",
      effectiveYearMonth: "",
      notes: "",
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-GB", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  const getRateStatus = (rate) => {
    if (rate.isCurrentRate) return "Current";
    if (rate.isFutureRate) return "Future";
    return "Past";
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Current":
        return { bg: "#dcfce7", text: "#166534" };
      case "Future":
        return { bg: "#fef3c7", text: "#92400e" };
      case "Past":
        return { bg: "#f3f4f6", text: "#6b7280" };
      default:
        return { bg: "#f3f4f6", text: "#6b7280" };
    }
  };

  const styles = {
    overlay: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1000,
      padding: "1rem",
    },
    modal: {
      backgroundColor: "white",
      borderRadius: "0.5rem",
      padding: "2rem",
      maxWidth: "800px",
      width: "100%",
      maxHeight: "90vh",
      overflow: "auto",
      boxShadow:
        "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
    },
    header: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      marginBottom: "1.5rem",
      borderBottom: "1px solid #f1f5f9",
      paddingBottom: "1rem",
    },
    title: {
      margin: 0,
      color: "#001272",
      fontSize: "1.5rem",
      fontWeight: "600",
    },
    closeButton: {
      background: "none",
      border: "none",
      fontSize: "1.5rem",
      cursor: "pointer",
      color: "#6b7280",
      padding: "0",
      width: "32px",
      height: "32px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: "0.25rem",
    },
    closeButtonHover: {
      backgroundColor: "#f3f4f6",
    },
    tutorInfo: {
      color: "#6b7280",
      marginBottom: "1.5rem",
      fontSize: "0.875rem",
    },
    section: {
      marginBottom: "2rem",
    },
    sectionTitle: {
      fontSize: "1.125rem",
      fontWeight: "600",
      color: "#001272",
      marginBottom: "1rem",
    },
    currentRateCard: {
      backgroundColor: "#f0f9ff",
      border: "1px solid #bae6fd",
      borderRadius: "0.5rem",
      padding: "1.5rem",
      marginBottom: "1.5rem",
    },
    currentRateAmount: {
      fontSize: "1.5rem",
      fontWeight: "600",
      color: "#001272",
      marginBottom: "0.5rem",
    },
    currentRateDetails: {
      fontSize: "0.875rem",
      color: "#6b7280",
      marginBottom: "0.25rem",
    },
    form: {
      backgroundColor: "#f8fafc",
      border: "1px solid #e2e8f0",
      borderRadius: "0.5rem",
      padding: "1.5rem",
      marginBottom: "1.5rem",
    },
    formTitle: {
      fontSize: "1rem",
      fontWeight: "600",
      color: "#001272",
      marginBottom: "1rem",
    },
    formGroup: {
      marginBottom: "1rem",
    },
    label: {
      display: "block",
      fontSize: "0.875rem",
      fontWeight: "500",
      color: "#001272",
      marginBottom: "0.25rem",
    },
    input: {
      width: "100%",
      padding: "0.5rem 0.75rem",
      border: "1px solid #d1d5db",
      borderRadius: "0.375rem",
      fontSize: "0.875rem",
      outline: "none",
      transition: "border-color 0.2s",
    },
    inputFocus: {
      borderColor: "#3b82f6",
      boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.1)",
    },
    textarea: {
      width: "100%",
      padding: "0.5rem 0.75rem",
      border: "1px solid #d1d5db",
      borderRadius: "0.375rem",
      fontSize: "0.875rem",
      minHeight: "80px",
      resize: "vertical",
      outline: "none",
      transition: "border-color 0.2s",
    },
    formActions: {
      display: "flex",
      gap: "0.75rem",
      justifyContent: "flex-end",
    },
    button: {
      padding: "0.5rem 1rem",
      border: "none",
      borderRadius: "0.375rem",
      fontSize: "0.875rem",
      fontWeight: "500",
      cursor: "pointer",
      transition: "background-color 0.2s",
    },
    primaryButton: {
      backgroundColor: "#001272",
      color: "white",
    },
    primaryButtonHover: {
      backgroundColor: "#000d5c",
    },
    secondaryButton: {
      backgroundColor: "#6b7280",
      color: "white",
    },
    secondaryButtonHover: {
      backgroundColor: "#4b5563",
    },
    dangerButton: {
      backgroundColor: "#dc2626",
      color: "white",
    },
    dangerButtonHover: {
      backgroundColor: "#b91c1c",
    },
    outlineButton: {
      backgroundColor: "transparent",
      color: "#001272",
      border: "1px solid #001272",
    },
    outlineButtonHover: {
      backgroundColor: "#f1f5f9",
    },
    historyTable: {
      width: "100%",
      borderCollapse: "collapse",
      fontSize: "0.875rem",
    },
    tableHeader: {
      backgroundColor: "#f8fafc",
      border: "1px solid #e2e8f0",
      padding: "0.75rem",
      textAlign: "left",
      fontSize: "0.75rem",
      fontWeight: "600",
      color: "#64748b",
      textTransform: "uppercase",
      letterSpacing: "0.05em",
    },
    tableCell: {
      border: "1px solid #e2e8f0",
      padding: "0.75rem",
      color: "#4b5563",
      verticalAlign: "top",
    },
    statusBadge: {
      display: "inline-block",
      padding: "0.25rem 0.5rem",
      borderRadius: "0.25rem",
      fontSize: "0.75rem",
      fontWeight: "500",
    },
    actionButtons: {
      display: "flex",
      gap: "0.5rem",
    },
    loading: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: "2rem",
      color: "#6b7280",
    },
    spinner: {
      border: "3px solid #f3f3f3",
      borderTop: "3px solid #53bdeb",
      borderRadius: "50%",
      width: "40px",
      height: "40px",
      animation: "spin 1s linear infinite",
      marginBottom: "1rem",
    },
    error: {
      backgroundColor: "#fee2e2",
      border: "1px solid #fca5a5",
      borderRadius: "0.375rem",
      padding: "1rem",
      color: "#991b1b",
      marginBottom: "1rem",
    },
    emptyState: {
      textAlign: "center",
      padding: "2rem",
      color: "#6b7280",
      backgroundColor: "#f8fafc",
      borderRadius: "0.5rem",
      border: "1px dashed #e2e8f0",
    },
  };

  const isLoading = isLoadingHistory || isLoadingCurrent || isLoadingSummary;
  const isMutating =
    createRateMutation.isPending ||
    updateRateMutation.isPending ||
    deleteRateMutation.isPending;

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <h2 style={styles.title}>Manage Rates for {tutorName}</h2>
          <button
            onClick={onClose}
            style={styles.closeButton}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor =
                styles.closeButtonHover.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = "";
            }}
            disabled={isMutating}
          >
            ×
          </button>
        </div>

        <div style={styles.tutorInfo}>Tutor ID: {tutorId}</div>

        {isLoading ? (
          <div style={styles.loading}>
            <div style={styles.spinner}></div>
            <p>Loading rate data...</p>
          </div>
        ) : (
          <>
            {/* Current Rate Section */}
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>Current Rate</h3>
              {currentError ? (
                <div style={styles.error}>
                  Error loading current rate: {currentError.message}
                </div>
              ) : currentRate ? (
                <div style={styles.currentRateCard}>
                  <div style={styles.currentRateAmount}>
                    {formatCurrency(currentRate.hourlyRate)}
                  </div>
                  <div style={styles.currentRateDetails}>
                    Effective:{" "}
                    {currentRate.formattedEffectiveDate ||
                      currentRate.effectiveYearMonth}
                  </div>
                  {currentRate.notes && (
                    <div style={styles.currentRateDetails}>
                      Notes: {currentRate.notes}
                    </div>
                  )}
                  <div style={styles.currentRateDetails}>
                    Last updated: {formatDate(currentRate.updatedAt)}
                  </div>
                </div>
              ) : (
                <div style={styles.emptyState}>
                  No current rate set for this tutor.
                </div>
              )}
            </div>

            {/* Rate Form Section */}
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>
                {editingRateId ? "Edit Rate" : "Add New Rate"}
              </h3>
              {isCreating || editingRateId ? (
                <div style={styles.form}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Hourly Rate *</label>
                    <input
                      type="number"
                      name="hourlyRate"
                      value={formData.hourlyRate}
                      onChange={handleInputChange}
                      placeholder="e.g., 6.00"
                      step="0.25"
                      min="5"
                      style={styles.input}
                      onFocus={(e) => {
                        e.target.style.borderColor =
                          styles.inputFocus.borderColor;
                        e.target.style.boxShadow = styles.inputFocus.boxShadow;
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = styles.input.borderColor;
                        e.target.style.boxShadow = "";
                      }}
                      disabled={isMutating}
                    />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Effective Year-Month *</label>
                    <input
                      type="month"
                      name="effectiveYearMonth"
                      value={formData.effectiveYearMonth}
                      onChange={handleInputChange}
                      style={styles.input}
                      onFocus={(e) => {
                        e.target.style.borderColor =
                          styles.inputFocus.borderColor;
                        e.target.style.boxShadow = styles.inputFocus.boxShadow;
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = styles.input.borderColor;
                        e.target.style.boxShadow = "";
                      }}
                      disabled={isMutating}
                    />
                    <div
                      style={{
                        fontSize: "0.75rem",
                        color: "#6b7280",
                        marginTop: "0.25rem",
                      }}
                    >
                      Format: YYYY-MM (e.g., 2024-01)
                    </div>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Notes (Optional)</label>
                    <textarea
                      name="notes"
                      value={formData.notes}
                      onChange={handleInputChange}
                      placeholder="Add any notes about this rate..."
                      style={styles.textarea}
                      onFocus={(e) => {
                        e.target.style.borderColor =
                          styles.inputFocus.borderColor;
                        e.target.style.boxShadow = styles.inputFocus.boxShadow;
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor =
                          styles.textarea.borderColor;
                        e.target.style.boxShadow = "";
                      }}
                      disabled={isMutating}
                    />
                  </div>

                  <div style={styles.formActions}>
                    {editingRateId ? (
                      <>
                        <button
                          style={{
                            ...styles.button,
                            ...styles.secondaryButton,
                          }}
                          onClick={handleCancelEdit}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor =
                              styles.secondaryButtonHover.backgroundColor;
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor =
                              styles.secondaryButton.backgroundColor;
                          }}
                          disabled={isMutating}
                        >
                          Cancel
                        </button>
                        <button
                          style={{ ...styles.button, ...styles.primaryButton }}
                          onClick={handleUpdateRate}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor =
                              styles.primaryButtonHover.backgroundColor;
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor =
                              styles.primaryButton.backgroundColor;
                          }}
                          disabled={isMutating}
                        >
                          {updateRateMutation.isPending
                            ? "Updating..."
                            : "Update Rate"}
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          style={{
                            ...styles.button,
                            ...styles.secondaryButton,
                          }}
                          onClick={handleCancelEdit}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor =
                              styles.secondaryButtonHover.backgroundColor;
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor =
                              styles.secondaryButton.backgroundColor;
                          }}
                          disabled={isMutating}
                        >
                          Cancel
                        </button>
                        <button
                          style={{
                            ...styles.button,
                            ...styles.primaryButton,
                          }}
                          onClick={handleCreateRate}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor =
                              styles.primaryButtonHover.backgroundColor;
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor =
                              styles.primaryButton.backgroundColor;
                          }}
                          disabled={isMutating}
                        >
                          {createRateMutation.isPending
                            ? "Creating..."
                            : "Create Rate"}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ) : (
                <div style={styles.formActions}>
                  <button
                    style={{ ...styles.button, ...styles.outlineButton }}
                    onClick={() => setIsCreating(true)}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor =
                        styles.outlineButtonHover.backgroundColor;
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor =
                        styles.outlineButton.backgroundColor;
                    }}
                  >
                    Add New Rate
                  </button>
                </div>
              )}
            </div>

            {/* Rate History Section */}
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>Rate History</h3>
              {historyError ? (
                <div style={styles.error}>
                  Error loading rate history: {historyError.message}
                </div>
              ) : rateHistory.length === 0 ? (
                <div style={styles.emptyState}>
                  No rate history available for this tutor.
                </div>
              ) : (
                <table style={styles.historyTable}>
                  <thead>
                    <tr>
                      <th style={styles.tableHeader}>Effective Date</th>
                      <th style={styles.tableHeader}>Rate</th>
                      <th style={styles.tableHeader}>Status</th>
                      <th style={styles.tableHeader}>Notes</th>
                      <th style={styles.tableHeader}>Created</th>
                      <th style={styles.tableHeader}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rateHistory.map((rate) => {
                      const status = getRateStatus(rate);
                      const statusColor = getStatusColor(status);
                      return (
                        <tr key={rate.id}>
                          <td style={styles.tableCell}>
                            {rate.formattedEffectiveDate ||
                              rate.effectiveYearMonth}
                          </td>
                          <td style={styles.tableCell}>
                            {formatCurrency(rate.hourlyRate)}
                          </td>
                          <td style={styles.tableCell}>
                            <span
                              style={{
                                ...styles.statusBadge,
                                backgroundColor: statusColor.bg,
                                color: statusColor.text,
                              }}
                            >
                              {status}
                            </span>
                          </td>
                          <td style={styles.tableCell}>{rate.notes || "—"}</td>
                          <td style={styles.tableCell}>
                            {formatDate(rate.createdAt)}
                          </td>
                          <td style={styles.tableCell}>
                            <div style={styles.actionButtons}>
                              <button
                                style={{
                                  ...styles.button,
                                  ...styles.outlineButton,
                                }}
                                onClick={() => handleEditRate(rate)}
                                onMouseEnter={(e) => {
                                  e.target.style.backgroundColor =
                                    styles.outlineButtonHover.backgroundColor;
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.backgroundColor =
                                    styles.outlineButton.backgroundColor;
                                }}
                                disabled={isMutating}
                              >
                                Edit
                              </button>
                              <button
                                style={{
                                  ...styles.button,
                                  ...styles.dangerButton,
                                }}
                                onClick={() => handleDeleteRate(rate.id)}
                                onMouseEnter={(e) => {
                                  e.target.style.backgroundColor =
                                    styles.dangerButtonHover.backgroundColor;
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.backgroundColor =
                                    styles.dangerButton.backgroundColor;
                                }}
                                disabled={isMutating || rate.isCurrentRate}
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}

        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }

            @media (max-width: 768px) {
              .modal {
                padding: 1rem;
              }

              .history-table {
                display: block;
                overflow-x: auto;
              }

              .action-buttons {
                flex-direction: column;
                gap: 0.25rem;
              }
            }
          `}
        </style>
      </div>
    </div>
  );
};

export default TutorRateManager;
